/**
 * Centralized SIM fixtures for the client console mock layer (`mockApi.ts`).
 *
 * Program Surfaces reuse the existing `MOCK_PROGRAM_SURFACES` from the Add
 * Position wizard so the two preview mechanisms stay consistent. Everything else is a minimal,
 * benign shape that satisfies the real response types and keeps each page
 * out of an error state.
 */

import type { ClientPositionView, CreatePositionInput, SavedDestination } from "@solomon-apps/shared";
import { YaasPositionStatus } from "@solomon-apps/shared";

import { MOCK_PROGRAM_SURFACES } from "@/pages/AddPosition/mockSimStates";
import type { ClientDashboard } from "@/types/clientDashboard";

import type { ClientEarningsResponse, MultisigPreflightResponse, MultisigsHistoryResponse } from "../clientPositions";
import type { DiscoveredPosition } from "../positionDiscovery";

export { MOCK_PROGRAM_SURFACES };

/** Mock Squads vault PDA for sim multisig positions. The real backend resolves
 *  a multisig account to its vault and returns it as `usdvBalanceAddress` (where
 *  the funds live); the sim mirrors that so the vault-aware balance/address code
 *  paths behave the same as against the live API. */
export const SIM_MULTISIG_VAULT_ADDRESS = "Vau1tMockAddre55Examp1eForSimMode000000000000002";

/** Mock Squads multisig account paired with `SIM_MULTISIG_VAULT_ADDRESS`. */
export const SIM_MULTISIG_ADDRESS = "Mu1tisigMockAddre55Examp1eForSimMode00000000001";

/** Deterministic identities for the explicit GOLD / USDv verification scenarios. */
export const SIM_GOLD_POOL_ADDRESS = "CpG1ZMHhK7iGKpvS1A6Ytf4hJbziY8xq1jKAX5GbfaZ2";
export const SIM_GOLD_SURFACE_UUID = "program-surface-mock-gold-usdv";
export const SIM_USDV_MINT = "USDvUSpnhCr9yBgj3UyVrD239HRUv4RsHwH2FxsWuMk";
export const SIM_GOLD_WALLET_ADDRESS = "BXq9CTas4DpibqUMCSbMHD5Q3rrNTx3JXekqC4GrKKHb";
export type GoldUsdvSimScenario = "mixed-gold-usdv" | "gold-usdv-only" | "gold-usdv-unavailable";

export function buildGoldUsdvDiscovery(
    addressSurfaceUuid: string,
    ownerAddress: string,
    scenario: GoldUsdvSimScenario
): {
    positions: DiscoveredPosition[];
    lpDiscovery: { status: "available"; complete: boolean; incompleteReason: string | null };
} {
    const observedAt = "2026-10-01T10:00:00.000Z";
    const lpPositions: DiscoveredPosition[] = ["42.75", "7.25"].map((amount, index) => ({
        version: 2,
        surfaceTypeSlug: "raydium-clmm",
        instance: {
            address:
                index === 0
                    ? "7Np41oeYqPefeNQEHSv1UDhYrehxin3NStELkfyBgPMd"
                    : "E9Yffvreri1KbHhE84J7UQoPjphL4KLC1ZwfE8PXWi7x"
        },
        sourceClassification: "eoa",
        squads: null,
        observedBalance: scenario === "gold-usdv-unavailable" && index === 0 ? null : amount,
        observedAt,
        lpPosition: {
            protocol: "raydium-clmm",
            poolAddress: SIM_GOLD_POOL_ADDRESS,
            positionAddress:
                index === 0
                    ? "7Np41oeYqPefeNQEHSv1UDhYrehxin3NStELkfyBgPMd"
                    : "E9Yffvreri1KbHhE84J7UQoPjphL4KLC1ZwfE8PXWi7x",
            observedAssetMint: SIM_USDV_MINT,
            holdingOwnerAddress: ownerAddress,
            pairLabel: "GOLD / USDv"
        },
        observation: {
            completeness: scenario === "gold-usdv-unavailable" && index === 0 ? "partial" : "complete",
            solanaSlot: "425000000",
            observedAt,
            fetchedAt: observedAt
        },
        match: { kind: "matched", programSurfaceUuid: SIM_GOLD_SURFACE_UUID },
        detectorVersion: "sim-gold-usdv-v1",
        confidence: null
    }));
    return {
        positions: [
            {
                version: 1,
                surfaceTypeSlug: "wallet-account",
                instance: { address: ownerAddress },
                sourceClassification: "eoa",
                squads: null,
                observedBalance: scenario === "gold-usdv-only" ? "0" : "1250",
                observedAt,
                match: { kind: "matched", programSurfaceUuid: addressSurfaceUuid },
                detectorVersion: null,
                confidence: null
            },
            ...lpPositions
        ],
        lpDiscovery: {
            status: "available",
            // Current Jachin responses positively identify holdings but cannot
            // attest that every NFT owned by the wallet was enumerated.
            complete: false,
            incompleteReason:
                scenario === "gold-usdv-unavailable" ? "position-amount-unavailable" : "owner_enumeration_unavailable"
        }
    };
}

/** Mirror post-enrollment reconciliation while retaining one record per NFT. */
export function buildGoldUsdvEnrolledPositions(
    input: CreatePositionInput,
    scenario: GoldUsdvSimScenario
): ClientPositionView[] {
    const base = buildCreatedPosition(input);
    const discovery = buildGoldUsdvDiscovery(input.programSurfaceUuid, input.walletAddress, scenario);
    return discovery.positions.map((holding, index) => ({
        ...base,
        uuid: `position-sim-gold-${input.walletAddress}-${index}`,
        status: YaasPositionStatus.Active,
        verifiedAt: "2026-10-01T10:00:00.000Z",
        sourceKind: "eoa",
        manualUsdvAmount: "0",
        ...(holding.lpPosition
            ? {
                  lpPosition: holding.lpPosition,
                  observedBalance: holding.observedBalance,
                  observedAt: holding.observedAt,
                  observation: holding.observation,
                  programSurface: {
                      uuid: SIM_GOLD_SURFACE_UUID,
                      name: "GOLD / USDv",
                      product: "GOLD / USDv",
                      surface: "Raydium CLMM",
                      type: "raydium-clmm",
                      address: SIM_GOLD_POOL_ADDRESS,
                      trailingApyPct: null
                  }
              }
            : {
                  programSurface: {
                      uuid: input.programSurfaceUuid,
                      name: "Address",
                      product: "USDv",
                      surface: "Address",
                      type: "wallet-account",
                      address: input.walletAddress,
                      trailingApyPct: null
                  }
              })
    }));
}

/** Two plausible saved reward destinations. */
export const MOCK_SAVED_DESTINATIONS: SavedDestination[] = [
    {
        uuid: "dest-mock-treasury",
        name: "Primary treasury wallet",
        address: "7Yaas3xConnectedWa11etAddre55Examp1e0000000001",
        createdAt: "2026-01-12T09:30:00.000Z",
        updatedAt: "2026-01-12T09:30:00.000Z"
    },
    {
        uuid: "dest-mock-ops",
        name: "Ops payout wallet",
        address: "0psPayoutWa11etAddre55Examp1eForSimMode00000002",
        createdAt: "2026-02-03T14:05:00.000Z",
        updatedAt: "2026-02-03T14:05:00.000Z"
    },
    {
        uuid: "dest-mock-growth",
        name: "Growth fund",
        address: "GrowthFundWa11etAddre55Examp1eForSimMode00000003",
        createdAt: "2026-02-20T10:00:00.000Z",
        updatedAt: "2026-02-20T10:00:00.000Z"
    },
    {
        uuid: "dest-mock-cold",
        name: "Cold storage reserve",
        address: "Co1dStorageReserveAddre55Examp1eForSim0000000004",
        createdAt: "2026-03-01T08:15:00.000Z",
        updatedAt: "2026-03-01T08:15:00.000Z"
    },
    {
        uuid: "dest-mock-validator",
        name: "Validator rewards",
        address: "Va1idatorRewardsWa11etAddre55Examp1eSim000000005",
        createdAt: "2026-03-18T16:45:00.000Z",
        updatedAt: "2026-03-18T16:45:00.000Z"
    }
];

/** Benign empty-ish earnings so the dashboard renders without erroring. */
export const MOCK_EARNINGS: ClientEarningsResponse = {
    daily: [],
    headline: {
        trailingApyPct: 0,
        apyDeltaPct: 0,
        earnedWindowUsdv: 0,
        earnedTotalUsdv: 0,
        earnedDeltaUsdv: 0
    },
    snapshotAt: "2026-06-08T00:00:00.000Z"
};

/** Benign client dashboard, consistent with `MOCK_EARNINGS`: no distribution
 *  has happened, so the realized APYs are unavailable (null). */
export const MOCK_CLIENT_DASHBOARD: ClientDashboard = {
    client: {
        uuid: "issuer-client-mock",
        slug: "sim-client",
        name: "Sim Client",
        status: "Active",
        contactEmail: "sim@example.com"
    },
    totalTvl: "0",
    activeAccounts: 0,
    rewardsEnabledAccounts: 0,
    trailingApy: { sevenDay: null, thirtyDay: null, allTime: null },
    totalEarned: { gross: "0", partnerFee: "0", net: "0" },
    lastDistributionAt: null
};

/**
 * Build a plausible `ClientPositionView` from a create-position request body.
 * Looks up the Program Surface in `MOCK_PROGRAM_SURFACES`; falls back to the first one when the
 * `programSurfaceUuid` isn't recognized so the response is always well-formed.
 */
export function buildCreatedPosition(input: CreatePositionInput): ClientPositionView {
    const programSurface =
        MOCK_PROGRAM_SURFACES.find((surface) => surface.uuid === input.programSurfaceUuid) ?? MOCK_PROGRAM_SURFACES[0];
    const now = "2026-06-08T00:00:00.000Z";
    return {
        uuid: `position-mock-${Math.random().toString(36).slice(2, 10)}`,
        status: YaasPositionStatus.Pending,
        verificationMode: input.verificationMode ?? null,
        manualReviewDescription: input.manualReviewDescription?.trim() || null,
        manualUsdvAmount: "0",
        totalRewards: "0",
        rewardsDestinationAddress: input.rewardsDestinationAddress ?? input.walletAddress,
        rejectionReason: null,
        rejectedAt: null,
        suspensionReason: null,
        suspendedAt: null,
        disabledAt: null,
        hiddenAt: null,
        verifiedAt: null,
        programSurface: {
            uuid: programSurface.uuid,
            name: programSurface.name,
            type: programSurface.type,
            address: programSurface.address,
            trailingApyPct: programSurface.trailingApyPct
        },
        // Multisig positions hold funds in the vault PDA, not the multisig
        // account, so the backend reports the vault as the balance address.
        // EOAs leave this unset and fall back to the account address.
        ...(input.sourceKind === "multisig-squads" ? { usdvBalanceAddress: SIM_MULTISIG_VAULT_ADDRESS } : {}),
        account: {
            uuid: `account-mock-${Math.random().toString(36).slice(2, 10)}`,
            label: input.label ?? "Sim wallet",
            address: input.walletAddress
        },
        createdAt: now,
        updatedAt: now
    };
}

/**
 * Build a `status: "ok"` multisig preflight from the request body. The
 * `signerAddress` (when present) is echoed back as a verify-capable member.
 * A signer-less preflight (the detection-time call) omits `members` entirely
 * — an empty list would derive every connected wallet as "not-member" and
 * dead-end the verify step; omitting it makes the flow re-preflight with the
 * connected wallet, which this mock then echoes back as a member.
 */
export function buildMultisigPreflight(signerAddress: string | null): MultisigPreflightResponse {
    return {
        status: "ok",
        multisigAddress: SIM_MULTISIG_ADDRESS,
        vaultAddress: SIM_MULTISIG_VAULT_ADDRESS,
        programId: "SQDS4ep65T869zMMBKyuUq6aD6EgTu8psMjkvj52pCfh",
        resolvedFrom: "multisig",
        memberCount: 1,
        threshold: 1,
        signerStatus: "can-verify",
        ...(signerAddress ? { members: [{ address: signerAddress, canVerify: true }] } : {})
    };
}

/** Empty multisigs history. */
export const MOCK_MULTISIGS_HISTORY: MultisigsHistoryResponse = {
    multisigs: []
};

/**
 * Build the discovery response for `POST /positions/discover` — one matched
 * wallet-account holding bound to the given Program Surface, enough to walk
 * the whole discovery flow (find → verify → register) offline. The sim's
 * `buildMultisigPreflight` classifies every source as the 1/1 mock multisig,
 * so discovery mirrors it: a Squads source whose holding lives at the mock
 * vault (the server-side canonicalization). The `DiscoveredPosition` contract
 * shape is a valid wire payload (the wire only relaxes it), so the sim
 * returns it directly. `observedBalance` is the display fallback only — the
 * FE's live balance read wins.
 */
export function buildDiscoveredPositions(programSurfaceUuid: string): {
    positions: DiscoveredPosition[];
} {
    return {
        positions: [
            {
                version: 1,
                surfaceTypeSlug: "wallet-account",
                instance: { address: SIM_MULTISIG_VAULT_ADDRESS },
                sourceClassification: "multisig-squads",
                squads: {
                    multisigAddress: SIM_MULTISIG_ADDRESS,
                    vaultAddress: SIM_MULTISIG_VAULT_ADDRESS,
                    threshold: 1,
                    memberCount: 1
                },
                observedBalance: "0",
                observedAt: "2026-06-08T00:00:00.000Z",
                match: { kind: "matched", programSurfaceUuid },
                detectorVersion: null,
                confidence: null
            }
        ]
    };
}
